import { AddToCart } from '@components/addtocart';
import { Star } from '@components/star';
import { TProduct, TVariant } from '@components/types';
import {
  inventoryByVariantId
} from '@components/variant/variantSelector';
import {
  getFirst,
  getImageObject,
  getLength,
  getSafeUrl,
  humanizeCurrency,
  sanityIoImageLoader,
  trimToLength
} from '@core/utils';
import { Store } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import { useState } from 'react';

type Props = {
  product: TProduct;
  showStoreName?: boolean;
};

export const SmallProductCard = ({ product, showStoreName = false }: Props) => {
  const [variant, setVariant] = useState<TVariant>(
    product?.variants?.find(
      (variant) =>
        getFirst(inventoryByVariantId(variant, product))?.is_available == true
    ) as TVariant
  );

  const store = product?.inventories?.[0]?.store;
  const isShopStore = store?.category_id === 1 || store?.category_id === undefined || store?.category_id === null;
  // A product with no store attached has no shop/store page to open, so it always goes to its own product page.
  const hasStore = store?.id > 0;
  const productHref = showStoreName && isShopStore && hasStore
    ? `/shop/${store?.id}/${getSafeUrl(store?.name)}?product_id=${product?.id}`
    : `/product/${product.id}/${getSafeUrl(product.name)}`;
  const storeHref = `/${isShopStore ? 'shop' : 'store'}/${store?.id}/${getSafeUrl(store?.name)}?product_id=${product?.id}`;
  const isAvailable = getLength(product?.inventories) > 0;

  return (
    <div className="box-border flex flex-row gap-3 bg-white rounded-2xl border border-neutral-200/70 shadow-sm hover:shadow-md transition-shadow overflow-hidden p-2.5">
      <div className="flex items-start justify-start flex-shrink-0">
        <Link href={productHref} prefetch={false}>
          <div className="relative w-24 h-24 md:w-28 md:h-28 rounded-xl overflow-hidden bg-neutral-100">
            <Image
              loader={sanityIoImageLoader}
              src={getImageObject(product.images)?.url}
              alt={
                getImageObject(product.images)?.description ||
                product.name ||
                'Product'
              }
              fill
              sizes="112px"
              className="object-cover"
            />
          </div>
        </Link>
      </div>

      <div className="flex flex-col gap-1 justify-between flex-1 min-w-0 py-0.5">
        <div className="flex flex-col gap-1 leading-tight font-manrope overflow-hidden">
          <Link href={productHref} prefetch={false}>
            <div className="font-semibold text-ellipsis text-sm text-neutral-900 font-manrope line-clamp-2">
              {trimToLength(product?.name, 40)}
            </div>
          </Link>
          {showStoreName && hasStore && store?.name && (
            <Link href={storeHref} prefetch={false}>
              <div className="flex items-center gap-1 text-xs text-neutral-500 font-medium">
                <Store size={11} className="flex-shrink-0" />
                <span className="line-clamp-1">{store?.name}</span>
              </div>
            </Link>
          )}
          <div className="flex gap-2 items-center flex-wrap text-sm">
            {trimToLength(variant?.name, 40) && (
              <div className="bg-success-100 rounded-full flex items-center justify-center px-2 py-0.5 text-xs text-success-500 font-semibold line-clamp-1">
                {trimToLength(variant?.name, 40)}
              </div>
            )}
            {product?.stats?.rating_overall ? (
              <Star rating={product?.stats?.rating_overall} />
            ) : null}
          </div>
          {isAvailable ? (
            <span className="text-business-500 font-bold lg:text-base text-sm">
              {humanizeCurrency(
                getFirst(product?.inventories)?.price || product?.mrp
              )}
            </span>
          ) : (
            <span className="text-xs font-semibold text-error-500 bg-error-100 w-fit px-2 py-0.5 rounded-full">Unavailable</span>
          )}
        </div>
        {isAvailable && (
          <div className="flex flex-row gap-4 items-center max-w-32">
            <AddToCart
              product={product}
              inventory={getFirst(product?.inventories)}
              isAvailable={getFirst(product?.inventories)?.is_available}
              btnStyle="text-business-500"
            ></AddToCart>
          </div>
        )}
      </div>
    </div>
  );
};
